<template>
    <div class="course" :class="[this.status ? 'enable' : 'disable']" @click="goCourse()">
        <div class="image-wrapper">
            <div class="image-bg" :style="imageStyle">
                <div class="degrade"></div>
                <div class="play-overlay" aria-hidden="true">
                    <span class="play-overlay__circle">
                        <svg
                            class="play-overlay__icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M9 7.5v9l7.5-4.5L9 7.5z"
                                fill="currentColor"
                            />
                        </svg>
                    </span>
                </div>
            </div>
        </div>
        <div class="infos-below">
            <div class="infos-below__inner">
                <div class="progress-block">
                    <div class="progress_course">
                        <div class="progress-container">
                            <div
                                class="progress-bar"
                                :style="{ width: progressPercentUi + '%' }"
                            ></div>
                        </div>
                        <span class="progress-label">{{ progressPercentUi }}%</span>
                    </div>
                </div>

                <h3 class="course-title">{{ title }}</h3>
                <p
                    v-if="courseDescription"
                    class="course-description"
                >
                    {{ courseDescription }}
                </p>

                <div
                    v-if="showCourseMeta"
                    class="course-meta"
                >
                    <ul class="course-meta__list">
                        <li
                            v-if="metaModuleQty != null"
                            class="course-meta__item"
                        >
                            <span class="course-meta__dt">Módulos</span>
                            <span class="course-meta__dd">{{ metaModuleQty }} {{ pluralModulos(metaModuleQty) }}</span>
                        </li>
                        <li
                            v-if="metaLessonQty != null"
                            class="course-meta__item"
                        >
                            <span class="course-meta__dt">Aulas</span>
                            <span class="course-meta__dd">{{ metaLessonQty }} {{ pluralAulas(metaLessonQty) }}</span>
                        </li>
                        <li
                            v-if="metaLessonTimeFormatted"
                            class="course-meta__item"
                        >
                            <span class="course-meta__dt">Carga horária</span>
                            <span class="course-meta__dd">{{ metaLessonTimeFormatted }}</span>
                        </li>
                        <li
                            v-if="metaDateEnrolFormatted"
                            class="course-meta__item"
                        >
                            <span class="course-meta__dt">Matrícula</span>
                            <span class="course-meta__dd">{{ metaDateEnrolFormatted }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    data() {
        return {};
    },
    computed: {
        imageStyle() {
            if (this.image && typeof this.image === 'string' && this.image.trim() !== '') {
                return {
                    backgroundImage: `url('${this.image}')`
                };
            }
            return {};
        },
        courseProgress() {
            // Prioriza progressPercentage do objeto course, depois progress do nível raiz, depois a prop progress
            if (this.data && this.data.course && this.data.course.progressPercentage != null) {
                return this.data.course.progressPercentage;
            }
            if (this.data && this.data.progress != null) {
                return this.data.progress;
            }
            if (this.progress != null) {
                return this.progress;
            }
            return 0;
        },
        /** Mesmo valor inteiro 0–100 para barra e texto (evita 60% com barra em 60,37%). */
        progressPercentUi() {
            const raw = this.courseProgress;
            const n = typeof raw === 'number' ? raw : parseFloat(raw);
            if (Number.isNaN(n)) {
                return 0;
            }
            return Math.min(100, Math.max(0, Math.round(n)));
        },
        courseDescription() {
            const raw = this.data?.course?.shortDescription;
            if (raw == null || String(raw).trim() === '') {
                return '';
            }
            const text = String(raw)
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            return text.length > 220 ? `${text.slice(0, 217)}…` : text;
        },
        metaModuleQty() {
            return this.safeNum(this.data?.course?.moduleQty ?? this.data?.moduleQty);
        },
        metaLessonQty() {
            return this.safeNum(this.data?.course?.lessonQty ?? this.data?.lessonQty);
        },
        metaLessonTimeFormatted() {
            const raw = this.data?.course?.lessonTime ?? this.data?.lessonTime;
            if (raw == null || raw === '') {
                return '';
            }
            return this.formatDurationSeconds(raw);
        },
        metaDateEnrolFormatted() {
            const raw = this.data?.course?.dateEnrol ?? this.data?.dateEnrol;
            return this.formatDateEnrol(raw);
        },
        showCourseMeta() {
            return (
                this.metaModuleQty != null ||
                this.metaLessonQty != null ||
                !!this.metaLessonTimeFormatted ||
                !!this.metaDateEnrolFormatted
            );
        }
    },
    methods: {
        ...mapActions(['storeCurrentCoursePortal']),
        goCourse() {
            this.storeCurrentCoursePortal({ currentCoursePortal: this.data });
            window.location.href = `/aluno/curso/${this.id}`;
        },
        safeNum(value) {
            if (value === undefined || value === null || value === '') {
                return null;
            }
            const n = Number(value);
            return Number.isNaN(n) ? null : n;
        },
        pluralModulos(n) {
            return Number(n) === 1 ? 'módulo' : 'módulos';
        },
        pluralAulas(n) {
            return Number(n) === 1 ? 'aula' : 'aulas';
        },
        formatDurationSeconds(value) {
            const sec = Number(value);
            if (Number.isNaN(sec) || sec < 0) {
                return '';
            }
            if (sec === 0) {
                return '0 min';
            }
            const h = Math.floor(sec / 3600);
            const m = Math.floor((sec % 3600) / 60);
            const parts = [];
            if (h > 0) {
                parts.push(`${h}h`);
            }
            if (m > 0 || h === 0) {
                parts.push(`${m} min`);
            }
            return parts.join(' ');
        },
        formatDateEnrol(value) {
            if (value == null || value === '') {
                return '';
            }
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) {
                return String(value);
            }
            return d.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
    },
    props: {
        image: {
            type: String,
            default: null
        },
        title: {
            type: String
        },
        progress: {
            type: Number
        },
        id: {
            Number
        },
        status: {
            Boolean
        },
        data: {
            Array
        }
    }
}
</script>
<style scoped>
.dark .course .course-title {
    color: var(--dash-text, #f3f4f6);
}

.dark .course .course-description {
    color: var(--dash-text-muted, #94a3b8);
}

.dark .course-meta__dt {
    color: var(--dash-text-muted, #94a3b8);
}

.dark .course-meta__dd {
    color: var(--dash-text, #e2e8f0);
}

.course {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    min-height: 300px;
    height: 100%;
    border-radius: 12px;
    cursor: pointer;
    overflow: hidden;
    position: relative;
    transition: box-shadow 0.25s ease, transform 0.25s ease;
    background: var(--dash-bg-elevated, #fff);
    text-align: left;
}

.dark .course {
    background: var(--dash-bg-elevated, #1a191f);
}

.course:hover {
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);
}

.dark .course:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
}

.course.enable:hover .play-overlay {
    background: rgba(15, 23, 42, 0.45);
}

.dark .course.enable:hover .play-overlay {
    background: rgba(0, 0, 0, 0.55);
}

.course.enable:hover .play-overlay__circle {
    transform: scale(1.08);
    box-shadow: 0 8px 28px rgba(25, 199, 208, 0.45);
}

.course.disable .play-overlay {
    opacity: 0.45;
    pointer-events: none;
}

.image-wrapper {
    width: 100%;
    flex: 1 1 auto;
    min-height: 180px;
    position: relative;
    max-height: 280px;
    overflow: hidden;
    border-radius: 12px 12px 0 0;
}

.image-bg {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    position: relative;
}

.degrade {
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 40%,
        rgba(0, 0, 0, 0.5) 100%
    );
    width: 100%;
    height: 100%;
    position: absolute;
    border-radius: 12px 12px 0 0;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: 1;
}

.play-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.28);
    transition: background 0.25s ease;
    pointer-events: none;
}

.play-overlay__circle {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.96);
    color: #0f766e;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.dark .play-overlay__circle {
    background: rgba(26, 25, 31, 0.92);
    color: #2dd4bf;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
}

.play-overlay__icon {
    width: 26px;
    height: 26px;
    margin-left: 3px;
}

.infos-below {
    width: 100%;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
    border-top: 1px solid var(--dash-border, rgba(15, 23, 42, 0.08));
}

.dark .infos-below {
    border-top-color: var(--dash-border, rgba(255, 255, 255, 0.08));
}

.infos-below__inner {
    padding: 16px 16px 18px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    flex: 1;
    box-sizing: border-box;
    width: 100%;
    text-align: left;
}

.progress-block {
    width: 100%;
    margin-bottom: 14px;
    align-self: stretch;
}

.course-title {
    color: var(--dash-text, #111827);
    font-family: Inter, system-ui, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: -0.02em;
    margin: 0 0 8px;
    width: 100%;
    text-align: left;
    align-self: stretch;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

.course-description {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: var(--dash-text-muted, #64748b);
    text-align: left;
    align-self: stretch;
    width: 100%;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

.course-meta {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--dash-border, rgba(15, 23, 42, 0.08));
    width: 100%;
    align-self: stretch;
}

.dark .course-meta {
    border-top-color: var(--dash-border, rgba(255, 255, 255, 0.08));
}

.course-meta__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
}

.course-meta__item {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: flex-start;
    gap: 6px 10px;
    font-size: 12px;
    line-height: 1.4;
}

.course-meta__dt {
    font-weight: 600;
    color: var(--dash-text-muted, #64748b);
    min-width: 5.5rem;
    flex-shrink: 0;
}

.course-meta__dd {
    color: var(--dash-text, #334155);
    font-variant-numeric: tabular-nums;
}

.progress_course {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    width: 100%;
    margin: 0;
    padding: 0;
    align-self: stretch;
}

.progress-container {
    width: 100%;
    height: 10px;
    background-color: #e5e7eb;
    border-radius: 6px;
    margin: 0;
    position: relative;
    overflow: hidden;
    /* box-shadow removido conforme solicitado */
    flex: 1 1 auto;
}

.dark .progress-container {
    background-color: #2d2d38;
}

.progress-bar {
    background-color: #1ac4cd;
    height: 100%;
    border-radius: 6px 0 0 6px;
    transition: width 0.4s cubic-bezier(0.4,0,0.2,1);
    min-width: 6px;    
    position: absolute;
    top: 0;
    margin: 0;
}

.progress-label {
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    color: #0d9488;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: 2.75rem;
    text-align: right;
}

.dark .progress-label {
    color: #2dd4bf;
}

@media (max-width: 768px) {
    .course {
        min-height: 260px;
    }
    .image-wrapper {
        min-height: 140px;
        flex: 1 1 auto;
        max-height: 220px;
    }
    .infos-below__inner {
        padding: 14px 14px 16px;
    }
    .progress-block {
        margin-bottom: 12px;
    }
    .course-title {
        font-size: 15px;
        margin-bottom: 6px;
    }
    .course-description {
        font-size: 12px;
        -webkit-line-clamp: 2;
    }
    .course-meta {
        margin-top: 10px;
        padding-top: 10px;
    }
    .course-meta__item {
        font-size: 11px;
    }
}

@media (max-width: 480px) {
    .course {
        min-height: auto;
        border-radius: 14px;
    }
    .image-wrapper {
        min-height: 132px;
        max-height: 200px;
    }
    .infos-below__inner {
        padding: 12px 12px 14px;
    }
    .course-meta__dt {
        min-width: 5rem;
    }
}
</style>